import { every, forEach, map, maxBy, toLower, trim } from 'lodash'
import {
  BlockType,
  CellBlock,
  CellValue,
  Cut,
  DataMatchOutput,
  DataMatchRowOutputs,
  DataMatchStatus,
  DateFormat,
  ExtractlyFileWithOCR,
  NormalizedFile,
  Row,
  TestDataOcr,
  TestFile,
  TextBlock,
  TextcutRectangle,
  UserSettings,
  ValueWaiter,
} from '../types'
import { isCell } from './guards'
import { getFileV2 } from '../api'
import {
  calculateHeightandWidth,
  findTOpLeftPointFromVertices,
  rotateOcrBox,
} from './spatial'
import { fetchPDFFromLocal, getNumericValue } from '../workbook'
import { PriorityQueue } from 'js-sdsl'
import numeral from 'numeral'
import {
  convertNumberToExcelDate,
  extractAndParseDateWithYearValidation,
  isValidDate,
} from './date-util'
import { buildNumberAndDateMap, buildSearchIndex } from './elastic-lunr'
import {
  isPossiblyNumeric,
  containsCurrencySymbol,
  extractNumericValueFromAmount,
} from './currency'
import { USER_SETTINGS } from '../constant'
import { levenshtein, normalizeHyphen, normalizeString } from './strings'
import { Result, ok, err } from 'neverthrow'
import { NormalizationError } from '../types/errors'

/**
 * OCR util function to break down the data
 * @param ocr
 * @returns pages, tables, cells, lines, words
 */
export const breakDownOcr = (
  ocr: TestDataOcr | undefined,
  degree: number
): [
  Map<string, TextBlock>,
  Map<string, TextBlock>,
  Map<string, TextBlock>,
  Map<string, TextBlock>,
  Map<string, TextBlock>
] => {
  const pages: TextBlock[] = []
  const pageMap = new Map<string, TextBlock>()
  const wordMap = new Map<string, TextBlock>()
  const lineMap = new Map<string, TextBlock>()
  const tableMap = new Map<string, TextBlock>()
  const cellMap = new Map<string, TextBlock>()
  if (!ocr) return [pageMap, tableMap, cellMap, lineMap, wordMap]
  // const copy = cloneDeep(ocr)
  const copy = deepCopyOcr(ocr) // performance improvement over cloneDeep
  for (const arr of copy) {
    const page = arr.textAnnotations.find(
      (block) => block.blockType === BlockType.PAGE
    )
    if (!page) continue
    const [topLeft, topRight, bottomRight, bottomLeft] =
      page.boundingPoly.vertices
    const [h, w] = calculateHeightandWidth(
      topLeft,
      topRight,
      bottomRight,
      bottomLeft
    )
    arr.textAnnotations.forEach((block) => {
      if (block.blockType !== BlockType.PAGE) {
        const newVertices = rotateOcrBox(
          block.boundingPoly.vertices,
          degree,
          w,
          h
        )
        block.boundingPoly.vertices = newVertices
      }
    })
  }
  forEach(copy, (item) => {
    forEach(item.textAnnotations, (block) => {
      if (block.blockType === BlockType.PAGE) pages.push(block)
      else if (block.blockType === BlockType.WORD) wordMap.set(block.id, block)
      else if (block.blockType === BlockType.LINE) lineMap.set(block.id, block)
      else if (block.blockType === BlockType.TABLE)
        tableMap.set(block.id, block)
      else if (block.blockType === BlockType.CELL) cellMap.set(block.id, block)
    })
  })

  forEach(pages, (page, i) => pageMap.set(String(i + 1), page))

  return [pageMap, tableMap, cellMap, lineMap, wordMap]
}

export const breakDownOcrByPage = (
  ocr: TestDataOcr | undefined,
  degree: number,
  currentPage: number
) => {
  const pageMap = new Map<string, TextBlock>()
  const wordMap = new Map<string, TextBlock>()
  const lineMap = new Map<string, TextBlock>()
  const tableMap = new Map<string, TextBlock>()
  const cellMap = new Map<string, TextBlock>()
  if (!ocr || !ocr.at(currentPage))
    return [pageMap, tableMap, cellMap, lineMap, wordMap]

  const page = ocr
    .at(currentPage)!
    .textAnnotations.find((block) => block.blockType === BlockType.PAGE)
  if (!page) return [pageMap, tableMap, cellMap, lineMap, wordMap]
  const [topLeft, topRight, bottomRight, bottomLeft] =
    page.boundingPoly.vertices
  const [h, w] = calculateHeightandWidth(
    topLeft,
    topRight,
    bottomRight,
    bottomLeft
  )
  ocr.at(currentPage)!.textAnnotations.forEach((block) => {
    if (block.blockType !== BlockType.PAGE) {
      const newVertices = rotateOcrBox(
        block.boundingPoly.vertices,
        degree,
        w,
        h
      )
      block.boundingPoly.vertices = newVertices
    }
  })

  forEach(ocr.at(currentPage)!.textAnnotations, (block) => {
    if (block.blockType === BlockType.PAGE)
      pageMap.set(currentPage + 1 + '', block)
    else if (block.blockType === BlockType.WORD) wordMap.set(block.id, block)
    else if (block.blockType === BlockType.LINE) lineMap.set(block.id, block)
    else if (block.blockType === BlockType.TABLE) tableMap.set(block.id, block)
    else if (block.blockType === BlockType.CELL) cellMap.set(block.id, block)
  })

  return [pageMap, tableMap, cellMap, lineMap, wordMap]
}

export const fillterDataBasedOnCutType = (
  page: TextBlock | undefined,
  tables: Map<string, TextBlock>,
  cells: Map<string, TextBlock>,
  lines: Map<string, TextBlock>,
  words: Map<string, TextBlock>
) => {
  const rects: TextBlock[] = []
  if (!page) return rects

  // if (type === Cut.TABLECUT || type === Cut.TABLES) {
  forEach(page.relationships, (re) => {
    forEach(re.ids, (id) => {
      if (tables.has(id)) {
        const table = tables.get(id)
        if (table) {
          rects.push(table)
          forEach(table.relationships, (re) => {
            forEach(re.ids, (id) => {
              const cell = cells.get(id)
              if (cell) {
                rects.push(cell)
              }
            })
          })
        }
      }
    })
  })
  // } else if (type === Cut.TEXTCUT || type === Cut.WORD || type === Cut.SUM) {
  forEach(page.relationships, (re) => [
    forEach(re.ids, (id) => {
      if (lines.has(id)) {
        const line = lines.get(id) as TextBlock
        forEach(line.relationships, (re) => {
          forEach(re.ids, (id) => {
            rects.push(words.get(id) as TextBlock)
          })
        })
      }
    }),
  ])
  // } else if (type === Cut.LINE) {
  //   forEach(page.relationships, (re) => {
  //     forEach(re.ids, (id) => {
  //       if (lines.has(id)) {
  //         const line = lines.get(id)
  //         if (line) rects.push(line)
  //       }
  //     })
  //   })
  // }
  return rects
}

/**
 * Transform text block to cell block
 * @param cells
 * @param words
 * @returns
 */
export const transformCells = (
  cells: TextBlock[],
  words: Map<string, TextBlock>
): CellBlock[] => {
  const bool = every(cells, (cell) => isCell(cell))
  if (!bool) return []
  const newCells = map(cells, (cell) => {
    const wordIds = cell?.relationships ? cell.relationships[0].ids : []
    const wordArray = map(wordIds, (id) => {
      const word = words.get(id)
      return word ? word.description : ''
    })
    const newCell = {
      ...cell,
      text: wordArray.length ? wordArray.join(' ') : '',
    }
    return newCell
  })
  return newCells as CellBlock[]
}

export const findRowAndColFromCells = (
  cells: CellBlock[]
): [row: number, col: number] => {
  const row = maxBy(cells, (cell) => cell.rowIndex)
  const col = maxBy(cells, (cell) => cell.columnIndex)
  return [row ? row.rowIndex : -1, col ? col.columnIndex : -1]
}

export const searchInputInFiles = async (
  files: TestFile[],
  input: any[][],
  primaryColumnIndices: number[],
  firstRowHeader: boolean,
  isLocalMode: boolean
): Promise<[any[][], number]> => {
  let hasSearched = 0
  const result: any[][] = []
  for (let i = 0; i < input.length; i++) {
    const tmp: any[] = []
    for (let j = 0; j < input[i].length; j++) {
      tmp.push(undefined)
      input[i][j] = String(input[i][j]).trim()
    }
    result.push(tmp)
  }
  if (firstRowHeader && input.length === 1) return [result, hasSearched]

  if (firstRowHeader) {
    const firstRow = [...input[0]]
    result[0] = firstRow
  }

  for (const file of files) {
    hasSearched++
    let ocr: TestDataOcr
    if (isLocalMode) {
      const f = await fetchPDFFromLocal(file.id)
      ocr = f?.ocr ?? []
    } else {
      const f = await getFileV2(file.id)
      ocr = f.ocr
    }

    const lines = ocr.map((r) => {
      const pageInfo = r.textAnnotations[0]
      const vs = pageInfo.boundingPoly.vertices
      const [h, w] = calculateHeightandWidth(vs[0], vs[1], vs[2], vs[3])
      return r.textAnnotations
        .filter((annotation) => annotation.blockType === BlockType.LINE)
        .map((block) => ({ ...block, ocrH: h, ocrW: w }))
    })
    const idx = firstRowHeader ? 1 : 0
    for (let i = idx; i < input.length; i++) {
      const resultRow = result[i]
      if (every(resultRow)) continue
      const searchValues = [...input[i]]
      const arr = searchInputFileHelper(searchValues, lines, file.id)
      // if (every(arr)) result[i] = arr
      if (
        resultRow.length === primaryColumnIndices.length ||
        primaryColumnIndices.length === 0
      ) {
        if (every(arr)) result[i] = arr
      } else {
        const middleMan: any[] = []
        primaryColumnIndices.forEach((index) => middleMan.push(arr[index]))
        if (every(middleMan)) result[i] = arr
      }
    }
  }
  return [result, hasSearched]
}

const searchInputFileHelper = (
  valueToSearch: any[],
  ocrWOrds: TextBlock[][],
  fileId: string
) => {
  const result = Array.from(valueToSearch).fill(undefined)
  for (let i = 0; i < result.length; i++) {
    for (let j = 0; j < ocrWOrds.length; j++) {
      for (let k = 0; k < ocrWOrds[j].length; k++) {
        if (
          ocrWOrds[j][k].description
            .toLocaleLowerCase()
            .includes(valueToSearch[i].toLocaleLowerCase())
        ) {
          // add file id and file page to the textblock
          result[i] = { ...ocrWOrds[j][k], fileId, filePage: j + 1 }
        }
        if (every(result)) return result
      }
    }
  }
  return result
}

export const pickUpTableCells = (
  tables: TextBlock[],
  cells: Map<string, TextBlock>,
  type: Cut
) => {
  if (type !== Cut.TABLECUT && type !== Cut.TABLES) return []
  const rects: TextBlock[] = []
  tables.forEach((table) => {
    const tableRelationship = table?.relationships?.at(0)
    if (tableRelationship) {
      const cellIds = tableRelationship.ids
      cellIds.forEach((id) => {
        const cell = cells.get(id)
        if (cell) {
          rects.push(cell)
        }
      })
    }
  })
  return rects
}

export const pickUpCellsFromTableReferences = (
  refs: TextcutRectangle[],
  tables: Map<string, TextBlock>,
  cells: Map<string, TextBlock>,
  fileId: string,
  filePage: number,
  showSelectionOnly: boolean,
  sheetId: string,
  rangeAddr: string
) => {
  const rects: TextBlock[] = []
  const ocrIds: string[] = []
  let filteredRefs = refs.filter(
    (ref) => ref.fileId === fileId && ref.filePage === filePage
  )

  if (showSelectionOnly)
    filteredRefs = filteredRefs.filter(
      (ref) => ref.sheetId === sheetId && ref.rangeAddr === rangeAddr
    )
  for (const ref of filteredRefs) {
    if (ref.ocrId) ocrIds.push(ref.ocrId)
  }
  const ocrIdSet = new Set(ocrIds)
  for (const tableId of ocrIdSet) {
    const table = tables.get(tableId)
    if (table) {
      const tableRelationship = table?.relationships?.at(0)
      if (tableRelationship) {
        const cellIds = tableRelationship.ids
        cellIds.forEach((id) => {
          const cell = cells.get(id)
          if (cell) {
            rects.push(cell)
          }
        })
      }
    }
  }
  return rects
}

export const createOutputPQ = (len: number): PriorityQueue<any[]>[] => {
  if (len < 0) throw new RangeError('Length cannot be negative')
  const arr = []
  for (let i = 0; i < len; i++) {
    arr[i] = new PriorityQueue<any[]>([], (a, b) => {
      const countA = a.reduce((acc, curr) => {
        if (curr === null) return acc + 1
        return acc
      }, 0)

      const countB = b.reduce((acc, curr) => {
        if (curr === null) return acc + 1
        return acc
      }, 0)

      return countA - countB
    })
  }
  return arr
}

export const previewOcr = (ocr: TestDataOcr) => {
  return ocr.reduce((a, b) => a + b.textPreview, '')
}

export const preProcessInput = (
  input: any[][],
  inputValues: any[][],
  dateFormat: DateFormat
) => {
  return input.map((row, r) =>
    row.map((val, c) => {
      const text = String(val).toLocaleLowerCase()
      const date = extractAndParseDateWithYearValidation(text, dateFormat)
      if (date) {
        const excelDate = convertNumberToExcelDate(inputValues[r][c])
        if (excelDate && excelDate === date) return excelDate
      }
      if (isPossiblyNumeric(text) || containsCurrencySymbol(text)) {
        return getNumericValue(text)
      }

      return text
    })
  )
}

export const processOutputPQ = (output: PriorityQueue<any[]>[]) => {
  const result: DataMatchOutput = []
  for (const row of output) {
    const processedResult = processSingleOutputPQ(row)
    result.push(processedResult)
  }
  // return result.map((v, index) => (v[index] === undefined ? '#Not found' : v))
  return result
}

export const processSingleOutputPQ = (
  pq: PriorityQueue<any[]>
): DataMatchRowOutputs => {
  if (!pq.size()) throw new Error('Invalid output priority queue')
  const top = pq.pop()
  if (!top) throw new Error('Invalid output priority queue')
  const slicedTop = top.slice(0, -1)
  const accuracy = calculateDataMatchAccuracy(slicedTop)
  if (accuracy.value() === 0) {
    return [slicedTop.map((_) => DataMatchStatus.NOT_FOUND)]
  }
  if (pq.size() === 0) {
    return [[...slicedTop]]
  }
  // return [...slicedTop]
  const findings = [[...slicedTop]]
  while (true) {
    const nextTop = pq.pop()
    if (!nextTop) throw new Error('Invalid output priority queue')
    const nextSlicedTop = nextTop.slice(0, -1)
    const nextAccuracy = calculateDataMatchAccuracy(nextSlicedTop)
    if (accuracy.value() !== nextAccuracy.value()) break
    let exactDataMatch = true
    for (let i = 0; i < slicedTop.length; i++) {
      if (
        (slicedTop[i] == null && nextSlicedTop[i] != null) ||
        (slicedTop[i] != null && nextSlicedTop[i] == null)
      )
        exactDataMatch = false
      break
    }
    if (!exactDataMatch) break
    findings.push([...nextSlicedTop])
    if (!pq.size()) break
  }
  return findings
  // const nextTop = pq.pop()
  // if (!nextTop) throw new Error('Invalid output priority queue')
  // const nextSlicedTop = nextTop.slice(0, -1)
  // const nextAccuracy = calculateDataMatchAccuracy(nextSlicedTop)
  // if (accuracy.value() === nextAccuracy.value()) {
  //   let exactDataMatch = true
  //   for (let i = 0; i < slicedTop.length; i++) {
  //     if (
  //       (slicedTop[i] === undefined && nextSlicedTop[i] !== undefined) ||
  //       (slicedTop[i] !== undefined && nextSlicedTop[i] === undefined)
  //     )
  //       exactDataMatch = false
  //   }
  //   if (exactDataMatch) {
  //     return slicedTop.map((_: any) => DataMatchStatus.DUPLICATE)
  //   }
  //   return [...slicedTop]
  // }
  // return [...slicedTop]
}

export const calculateDataMatchAccuracy = (row: any[]) => {
  const result = row.reduce((a, b) => {
    return b === null ? a : a + 1
  }, 0)
  return numeral(result).divide(row.length)
}

export const dataMatchFilterItems =
  (
    dateMap: Map<string, string>,
    numberMap: Map<string, string>,
    beenUsed: Set<string>
  ) =>
  (searchInput: string, rawInput: string) =>
  (item: TextBlock | null): boolean => {
    if (!item || beenUsed.has(item.id)) return false
    const normalizedDescription = normalizeString(item.description, [
      toLower,
      trim,
      normalizeHyphen,
    ])
    const normalizedSearchInput = normalizeString(searchInput, [
      toLower,
      trim,
      normalizeHyphen,
    ])
    const normalizedRawInput = normalizeString(rawInput, [
      toLower,
      trim,
      normalizeHyphen,
    ])
    if (
      normalizedSearchInput &&
      normalizedDescription.includes(normalizedSearchInput)
    ) {
      return true
    }
    if (
      normalizedRawInput &&
      normalizedDescription.includes(normalizedRawInput)
    ) {
      return true
    }

    if (dateMap.has(item.id) && dateMap.get(item.id) === searchInput) {
      return true
    }
    if (
      numberMap.has(item.id) &&
      (isPossiblyNumeric(item.description) ||
        containsCurrencySymbol(item.description)) &&
      numberMap.get(item.id) === searchInput
    ) {
      return true
    }
    return false
  }

/**
 * Performs advanced data matching between input data and OCR-extracted text from files.
 * This is the main entry point for the data matching system that combines multiple sophisticated
 * algorithms to find and match text content from documents with user-provided input data.
 *
 * The function implements a multi-stage matching process:
 * 1. Creates row state machines from input data for efficient tracking
 * 2. Builds value waiters to map search values to their expected positions
 * 3. Normalizes OCR files for optimized text searching
 * 4. Performs intelligent matching with scoring and priority handling
 * 5. Returns matched TextBlocks or null for unmatched cells
 *
 * Special features:
 * - Handles currency values with automatic numeric extraction
 * - Processes dates with format validation and normalization
 * - Supports primary column matching for data validation
 * - Implements scoring algorithms for match quality assessment
 * - Prevents duplicate matches across multiple files
 *
 * @param files - Array of files containing OCR data to search through
 * @param input - 2D array of preprocessed input values to match against OCR content
 * @param inputValues - 2D array of raw input values (before preprocessing) for reference
 * @param primaryIndices - Array of column indices that must have matches for row validation
 * @param firstRowHeader - Flag indicating if first row should be treated as header (1) or data (0)
 *
 * @returns Result containing either:
 *   - Success: 2D array where each cell contains a matched TextBlock, null (no match), or string (header)
 *   - Error: Detailed error information about what went wrong in the matching process
 *
 * @throws {Error} When input validation fails or internal processing encounters errors
 *
 * @example
 * ```typescript
 * // Basic data matching without primary columns
 * const files = [{ id: 'doc1', ocrJson: [...], dateCache: [...] }]
 * const input = [['John', 'Doe', '30'], ['Jane', 'Smith', '25']]
 * const inputValues = [['John', 'Doe', 30], ['Jane', 'Smith', 25]]
 *
 * const result = dataMatch(files, input, inputValues, [], 0)
 * if (result.isOk()) {
 *   const matches = result.value
 *   // matches[0][0] = TextBlock for 'John' or null if not found
 *   // matches[0][1] = TextBlock for 'Doe' or null if not found
 * }
 *
 * // With header row and primary column validation
 * const withHeader = [['Name', 'Surname', 'Age'], ['John', 'Doe', '30']]
 * const withHeaderValues = [['Name', 'Surname', 'Age'], ['John', 'Doe', 30]]
 * const primaryColumns = [0] // Name column must match
 *
 * const headerResult = dataMatch(files, withHeader, withHeaderValues, primaryColumns, 1)
 * if (headerResult.isOk()) {
 *   const matches = headerResult.value
 *   // matches[0] = ['Name', 'Surname', 'Age'] (header preserved as strings)
 *   // matches[1] = [TextBlock|null, TextBlock|null, TextBlock|null] (data row)
 * }
 *
 * // Currency and date handling
 * const financialData = [['$1,000.50', '2023-12-25'], ['€500.00', '01/01/2024']]
 * const financialValues = [['$1,000.50', '2023-12-25'], ['€500.00', '01/01/2024']]
 *
 * const financialResult = dataMatch(files, financialData, financialValues, [], 0)
 * // Automatically handles currency extraction (1000.5) and date normalization
 * ```
 *
 * @remarks
 * **Performance Considerations:**
 * - Uses Map-based lookups for O(1) performance on large datasets
 * - Implements early termination for impossible matches
 * - Optimizes memory usage with efficient data structures
 *
 * **Matching Algorithm:**
 * - Scores potential matches based on how many values from the same row are found
 * - Prioritizes matches with higher accuracy scores
 * - Handles duplicate values intelligently across multiple files
 * - Supports fuzzy matching through text normalization
 *
 * **Error Handling:**
 * - Returns detailed error information for debugging
 * - Gracefully handles malformed OCR data
 * - Validates all input parameters before processing
 * - Provides rollback capability for failed operations
 *
 * @see {@link createRowStateMachineFromInput} For row state machine creation
 * @see {@link createValueWaiters} For value waiter mapping
 * @see {@link normalizeMultipleFiles} For OCR file normalization
 * @see {@link matchFiles} For the core matching algorithm
 *
 * @since 1.0.0
 * @version 2.0.0 - Added currency and date handling, improved error handling
 */
export const dataMatch = (
  files: ExtractlyFileWithOCR[],
  input: any[][],
  inputValues: any[][],
  primaryIndices: number[],
  firstRowHeader: 0 | 1
): Result<(TextBlock | null | string)[][], Error> => {
  const rowStatesResult = createRowStateMachineFromInput(
    input,
    inputValues,
    firstRowHeader
  )
  if (rowStatesResult.isErr()) {
    return err(rowStatesResult.error)
  }
  const rowStates = rowStatesResult.value

  const createValueWaiterResult = createValueWaiters(rowStates)
  if (createValueWaiterResult.isErr()) {
    return err(createValueWaiterResult.error)
  }
  const valueWaiter = createValueWaiterResult.value

  const normalizedFilesResult = normalizeMultipleFiles(files)

  if (normalizedFilesResult.isErr()) {
    return err(new Error(normalizedFilesResult.error))
  }

  const textblockMap = new Map<string, TextBlock>()
  normalizedFilesResult.value.successful.forEach((file) => {
    file.lines.forEach((line) => textblockMap.set(line.id, line))
    file.words.forEach((word) => textblockMap.set(word.id, word))
  })

  // Use the new matchFiles function with proper error handling
  const matchFilesResult = matchFiles({
    rowStates,
    filesResult: normalizedFilesResult.value.successful,
    valueWaiter,
    primaryIndices,
  })

  if (matchFilesResult.isErr()) {
    return err(matchFilesResult.error)
  }

  const result = rowStates.map((row) => {
    if (row.type === 'HEADER') {
      return [...row.cells] as string[]
    }
    return row.cells.map((cell) => {
      if (cell && typeof cell === 'object' && 'id' in cell) {
        const textBlock = textblockMap.get(cell.id)
        if (textBlock) {
          return textBlock
        }
        return null
      }
      return null
    }) as (TextBlock | null)[]
  })
  return ok(result)
}

export const dataMatchWithDuplicateHandling = (
  files: ExtractlyFileWithOCR[],
  input: any[][],
  inputValues: any[][],
  primaryIndices: number[],
  firstRowHeader: 0 | 1
): DataMatchOutput => {
  const settings = localStorage.getItem(USER_SETTINGS)
  const userSettings = settings ? (JSON.parse(settings) as UserSettings) : null
  const dateFormat = userSettings ? userSettings.dateFormat : DateFormat.US
  const actualInput = firstRowHeader ? input.slice(1, input.length) : input
  const actualInputValues = firstRowHeader
    ? inputValues.slice(1, input.length)
    : inputValues
  const processedInput = preProcessInput(
    actualInput,
    actualInputValues,
    dateFormat
  )
  const output: PriorityQueue<any[]>[] = createOutputPQ(processedInput.length)
  for (const file of files) {
    const ocr = file.ocrJson
    const dateCache = file.dateCache ? file.dateCache : []
    const [pages, , , lines, words] = breakDownOcr(ocr, 0)
    const { dateMap, numberMap } = buildNumberAndDateMap(
      lines,
      words,
      dateCache
    )
    const searchIndex = buildSearchIndex(words, lines, dateCache)
    // memo values that have been matched
    const beenUsed = new Set<string>()
    const memoDateMapNumberMapBeenUsedFunc = dataMatchFilterItems(
      dateMap,
      numberMap,
      beenUsed
    )
    for (let j = 0; j < processedInput.length; j++) {
      const inputRow = processedInput[j]
      const outputQueue = output[j]
      const result: any[] = inputRow.map(() => null)
      for (let i = 0; i < inputRow.length; i++) {
        const searchInput = String(inputRow[i])
        const rawInput = String(actualInputValues[j][i])
        if (searchInput === '') {
          result[i] = ''
          continue
        }
        const response = searchIndex.search(searchInput, {})
        const mapped = response
          .map((re) => {
            if (words.has(re.ref)) return words.get(re.ref)!
            if (lines.has(re.ref)) return lines.get(re.ref)!
            return null
          })
          .filter((item) => item !== null) as unknown as TextBlock[]
        const filterFunc = memoDateMapNumberMapBeenUsedFunc(
          searchInput,
          rawInput
        )
        // let filteredItem = mapped.find(filterFunc)
        const filteredItems = mapped.filter(filterFunc)
        let filteredItem = filteredItems.length
          ? filteredItems.reduce((acc, curr) => {
              const accLev = levenshtein(acc.description, searchInput)
              const currLev = levenshtein(curr.description, searchInput)
              return currLev < accLev ? curr : acc
            })
          : undefined

        // If item is not found, check if searchInput is a date
        // if it is a date, check if it is in the dateMap
        // if it is, return the item
        if (!filteredItem) {
          const date = extractAndParseDateWithYearValidation(
            searchInput,
            dateFormat
          )
          if (date) {
            const blocks: TextBlock[] = []
            for (const [k, v] of dateMap) {
              if (v === date) {
                let item = words.get(k)
                if (!item) item = lines.get(k)
                if (item) {
                  blocks.push(item)
                }
              }
            }
            filteredItem = blocks.length
              ? blocks.reduce((acc, curr) => {
                  const accLev = levenshtein(acc.description, searchInput)
                  const currLev = levenshtein(curr.description, searchInput)
                  return currLev < accLev ? curr : acc
                })
              : undefined
          }
        }

        if (filteredItem) {
          // beenUsed.add(filteredItem.id)
          const [page, pageW, pageH] = getTextblockPage(
            pages,
            lines,
            filteredItem
          )
          result[i] = {
            ...filteredItem,
            fileId: file.id,
            filePage: page,
            pageH,
            pageW,
          }
        }
      }
      result.push(false)
      if (!primaryIndices.length) {
        outputQueue.push(result)
        const allTrue = result.every(Boolean)
        if (allTrue) {
          result.slice(0, result.length - 1).forEach((item) => {
            beenUsed.add(item.id)
          })
        }
      } else {
        let passPrimaryIndicesCheck = true
        for (const index of primaryIndices) {
          if (result[index] === null) {
            passPrimaryIndicesCheck = false
            break
          }
        }
        if (passPrimaryIndicesCheck) {
          outputQueue.push(result)
          result.slice(0, result.length - 1).forEach((item) => {
            beenUsed.add(item.id)
          })
        } else outputQueue.push(result.map((_) => null))
      }
    }
  }
  const outputResult = processOutputPQ(output)

  if (firstRowHeader) {
    outputResult.unshift([[...input[0]]])
  }
  return outputResult
}

export const getTextblockPage = (
  pages: Map<string, TextBlock>,
  lines: Map<string, TextBlock>,
  block: TextBlock
) => {
  const page = -1
  for (const [k, v] of pages) {
    const pageW = v.boundingPoly.vertices[1].x - v.boundingPoly.vertices[0].x
    const pageH = v.boundingPoly.vertices[3].y - v.boundingPoly.vertices[0].y
    const children = v.relationships?.find((elem) => elem.type === 'CHILD')
    if (!children) continue
    for (const id of children.ids) {
      const line = lines.get(id)
      if (!line) continue
      if (line.id === block.id) return [Number(k), pageW, pageH]
      const lineChildren = line.relationships?.find(
        (elem) => elem.type === 'CHILD'
      )
      if (!lineChildren) continue
      if (lineChildren.ids.includes(block.id)) {
        return [Number(k), pageW, pageH]
      }
    }
  }

  return [page, 0, 0]
}

type SnipPossibleContinuousTablesArgs = {
  table: TextBlock
  filePage: number
  pages: Map<string, TextBlock>
  tables: Map<string, TextBlock>
  cells: Map<string, TextBlock>
  words: Map<string, TextBlock>
}

export const snipPossibleContinuousTables = (
  args: SnipPossibleContinuousTablesArgs
) => {
  const { table, cells, filePage, pages, tables, words } = args
  const headerTable = tableHasHeader({ table, cells })
  const tablesInPage = getAllTablesInPage(args)
  const tablesInPageLength = tablesInPage.length
  table.filePage = filePage
  if (!tablesInPageLength)
    throw new Error('Internal Error: snipPossibleContinuousTables')
  else if (tablesInPageLength === 1) {
    // TODO: Only table in the page, do a two way search
    if (headerTable) {
      const [prev, next] = twoWaySearchWithHeader({
        cells,
        filePage,
        pages,
        table,
        tables,
        words,
      })
      console.log([...prev, table, ...next])
      return [...prev, table, ...next]
    } else {
      const [prev, next] = twoWaySearchWithoutHeader({
        cells,
        filePage,
        pages,
        table,
        tables,
        words,
      })
      console.log([...prev, table, ...next])
      return [...prev, table, ...next]
    }
  } else {
    // TODO: if the table is the last table in the page, it must have a header to be considered as a continuous table
    if (tablesInPage[tablesInPageLength - 1].id === table.id) {
      if (!headerTable) return [table]
      else {
        // TODO: search next page
        const possibleTables: TextBlock[] = []
        searchNextPageTablesWithHeader({
          cells,
          pages,
          possibleTables,
          searchPage: filePage + 1,
          table,
          tables,
          words,
        })
        console.log([table, ...possibleTables])
        return [table, ...possibleTables]
      }
    } else if (tablesInPage[0].id !== table.id) {
      return [table]
    } else {
      // TODO: search prev page
      const possibleTables: TextBlock[] = []
      searchPrevPageTables({
        cells,
        pages,
        possibleTables,
        searchPage: filePage - 1,
        table,
        tables,
        words,
      })
      possibleTables.reverse()
      console.log([...possibleTables, table])
      return [...possibleTables, table]
    }
  }
}

type GetAllTablesInPageArgs = Omit<
  SnipPossibleContinuousTablesArgs,
  'table' | 'cells' | 'words'
>
const getAllTablesInPage = (args: GetAllTablesInPageArgs): TextBlock[] => {
  const { filePage, pages, tables } = args
  const page = pages.get(filePage + '')
  if (!page) return []
  const tablesInPage: TextBlock[] = []
  const pageChild = page.relationships?.find((re) => re.type === 'CHILD')
  pageChild?.ids.forEach((id) => {
    if (tables.has(id)) tablesInPage.push(tables.get(id)!)
  })
  return tablesInPage
}

type TableHasHeaderArgs = Pick<
  SnipPossibleContinuousTablesArgs,
  'table' | 'cells'
>
const tableHasHeader = ({ table, cells }: TableHasHeaderArgs) => {
  const tableChild = table.relationships?.find((re) => re.type === 'CHILD')
  if (!tableChild) return false
  return tableChild.ids.some((id) => {
    const cell = cells.get(id)
    return cell && cell.entityTypes?.includes('COLUMN_HEADER')
  })
}

type GetTableHeadersArgs = Pick<
  SnipPossibleContinuousTablesArgs,
  'table' | 'cells' | 'words'
>
const getTableHeaders = ({
  table,
  cells,
  words,
}: GetTableHeadersArgs): string | null => {
  const tableChild = table.relationships?.find((re) => re.type === 'CHILD')
  if (!tableChild) return null
  const headers = tableChild.ids
    .filter((id) => {
      if (!cells.has(id)) return false
      const cell = cells.get(id)!
      return cell.entityTypes?.includes('COLUMN_HEADER')
    })
    .map((id) => cells.get(id)!)
    .sort((a, b) => a.rowIndex! - b.rowIndex!)
  const headerStrings: string[] = []
  for (const header of headers) {
    const headerChild = header.relationships?.find((re) => re.type === 'CHILD')
    if (!headerChild) continue
    headerChild.ids.forEach((id) => {
      const word = words.get(id)
      if (word) headerStrings.push(word.description)
    })
  }
  return headerStrings.join('')
}

type SearchNextPageTablesArgs = Omit<
  SnipPossibleContinuousTablesArgs,
  'filePage'
> & { searchPage: number; possibleTables: TextBlock[] }
const searchNextPageTablesWithHeader = ({
  cells,
  pages,
  searchPage,
  tables,
  possibleTables,
  table,
  words,
}: SearchNextPageTablesArgs) => {
  const hasPage = pages.has(searchPage + '')
  if (!hasPage) return
  const tableHeader = getTableHeaders({ table, cells, words })
  const tableVertices = table.boundingPoly.vertices
  const [, width] = calculateHeightandWidth(
    tableVertices[0],
    tableVertices[1],
    tableVertices[2],
    tableVertices[3]
  )
  const topLeft = findTOpLeftPointFromVertices(tableVertices)
  if (!topLeft) return
  const tablesInPage = getAllTablesInPage({
    filePage: searchPage,
    pages,
    tables,
  })
  if (!tablesInPage.length) return
  else {
    const pageTable = tablesInPage[0]
    const pageInTableHeader = getTableHeaders({
      table: pageTable,
      cells,
      words,
    })
    const pageTableVertices = pageTable.boundingPoly.vertices
    const [, pageTableWidth] = calculateHeightandWidth(
      pageTableVertices[0],
      pageTableVertices[1],
      pageTableVertices[2],
      pageTableVertices[3]
    )
    const pageTableTopLeft = findTOpLeftPointFromVertices(pageTableVertices)
    if (!pageTableTopLeft) return
    if (pageInTableHeader) {
      if (pageInTableHeader === tableHeader) {
        pageTable.filePage = searchPage
        possibleTables.push(pageTable)
        if (tablesInPage.length === 1) {
          searchNextPageTablesWithHeader({
            cells,
            pages,
            possibleTables,
            searchPage: searchPage + 1,
            table: pageTable,
            tables,
            words,
          })
        }
      }
    } else {
      if (
        isDifferenceLessThan10Percent(width, pageTableWidth) &&
        isDifferenceLessThan10Percent(pageTableTopLeft.x, topLeft.x)
      ) {
        pageTable.filePage = searchPage
        possibleTables.push(pageTable)
        if (tablesInPage.length === 1) {
          searchNextPageTablesWithHeader({
            cells,
            pages,
            possibleTables,
            searchPage: searchPage + 1,
            table: pageTable,
            tables,
            words,
          })
        }
      }
    }
  }
}

const isDifferenceLessThan10Percent = (num1: number, num2: number) => {
  const difference = Math.abs(num1 - num2)
  const largerNumber = Math.max(num1, num2)
  const threshold = largerNumber / 10
  return difference < threshold
}

type TwoWaySearchWithoutHeaderArgs = SnipPossibleContinuousTablesArgs
const twoWaySearchWithoutHeader = ({
  cells,
  filePage,
  pages,
  table,
  tables,
  words,
}: TwoWaySearchWithoutHeaderArgs): [TextBlock[], TextBlock[]] => {
  const possiblePreTables: TextBlock[] = []
  const possibleNextTables: TextBlock[] = []

  twoWaySearchWithoutHeaderPrevTableHelper({
    cells,
    pages,
    possibleTables: possiblePreTables,
    searchPage: filePage - 1,
    table,
    tables,
    words,
  })

  twoWaySearchWithoutHeaderNextTableHelper({
    cells,
    pages,
    possibleTables: possibleNextTables,
    searchPage: filePage + 1,
    table,
    tables,
    words,
  })

  possiblePreTables.reverse()
  return [possiblePreTables, possibleNextTables]
}

type TwoWaySearchWithoutHeaderPrevTableHelperArgs = SearchNextPageTablesArgs
const twoWaySearchWithoutHeaderPrevTableHelper = ({
  cells,
  pages,
  possibleTables,
  searchPage,
  table,
  tables,
  words,
}: TwoWaySearchWithoutHeaderPrevTableHelperArgs) => {
  const hasPage = pages.has(searchPage + '')
  if (!hasPage) return
  const tableVertices = table.boundingPoly.vertices
  const [, width] = calculateHeightandWidth(
    tableVertices[0],
    tableVertices[1],
    tableVertices[2],
    tableVertices[3]
  )
  const topLeft = findTOpLeftPointFromVertices(tableVertices)
  if (!topLeft) return
  const tablesInPage = getAllTablesInPage({
    filePage: searchPage,
    pages,
    tables,
  })
  if (!tablesInPage.length) return
  else {
    const pageTable = tablesInPage[tablesInPage.length - 1]
    const pageTableHeader = getTableHeaders({
      table: pageTable,
      cells,
      words,
    })
    const pageTableVertices = pageTable.boundingPoly.vertices
    const [, pageTableWidth] = calculateHeightandWidth(
      pageTableVertices[0],
      pageTableVertices[1],
      pageTableVertices[2],
      pageTableVertices[3]
    )
    const pageTableTopLeft = findTOpLeftPointFromVertices(pageTableVertices)
    if (!pageTableTopLeft) return

    if (
      isDifferenceLessThan10Percent(width, pageTableWidth) &&
      isDifferenceLessThan10Percent(pageTableTopLeft.x, topLeft.x)
    ) {
      if (pageTableHeader) {
        pageTable.filePage = searchPage
        possibleTables.push(pageTable)
      } else {
        if (tablesInPage.length === 1) {
          pageTable.filePage = searchPage
          possibleTables.push(pageTable)
          twoWaySearchWithoutHeaderPrevTableHelper({
            cells,
            pages,
            possibleTables,
            searchPage: searchPage - 1,
            table: pageTable,
            tables,
            words,
          })
        }
      }
    }
  }
}

type TwoWaySearchWithoutHeaderNextTableHelper = SearchNextPageTablesArgs
const twoWaySearchWithoutHeaderNextTableHelper = ({
  cells,
  pages,
  possibleTables,
  searchPage,
  table,
  tables,
  words,
}: TwoWaySearchWithoutHeaderNextTableHelper) => {
  const hasPage = pages.has(searchPage + '')
  if (!hasPage) return
  const tableVertices = table.boundingPoly.vertices
  const [, width] = calculateHeightandWidth(
    tableVertices[0],
    tableVertices[1],
    tableVertices[2],
    tableVertices[3]
  )
  const topLeft = findTOpLeftPointFromVertices(tableVertices)
  if (!topLeft) return
  const tablesInPage = getAllTablesInPage({
    filePage: searchPage,
    pages,
    tables,
  })
  if (!tablesInPage.length) return
  else {
    const pageTable = tablesInPage[0]
    const pageTableHeader = getTableHeaders({
      table: pageTable,
      cells,
      words,
    })
    const pageTableVertices = pageTable.boundingPoly.vertices
    const [, pageTableWidth] = calculateHeightandWidth(
      pageTableVertices[0],
      pageTableVertices[1],
      pageTableVertices[2],
      pageTableVertices[3]
    )
    const pageTableTopLeft = findTOpLeftPointFromVertices(pageTableVertices)
    if (!pageTableTopLeft) return

    if (
      isDifferenceLessThan10Percent(width, pageTableWidth) &&
      isDifferenceLessThan10Percent(pageTableTopLeft.x, topLeft.x)
    ) {
      if (!pageTableHeader) {
        pageTable.filePage = searchPage
        possibleTables.push(pageTable)
        if (tablesInPage.length === 1) {
          twoWaySearchWithoutHeaderNextTableHelper({
            cells,
            pages,
            possibleTables,
            searchPage: searchPage + 1,
            table: pageTable,
            tables,
            words,
          })
        }
      }
    }
  }
}

type TwoWaySearchWithHeaderArgs = SnipPossibleContinuousTablesArgs
const twoWaySearchWithHeader = ({
  cells,
  filePage,
  pages,
  table,
  tables,
  words,
}: TwoWaySearchWithHeaderArgs): [TextBlock[], TextBlock[]] => {
  const possiblePreTables: TextBlock[] = []
  const possibleNextTables: TextBlock[] = []

  twoWaySearchWithHeaderPrevHelper({
    cells,
    pages,
    possibleTables: possiblePreTables,
    searchPage: filePage - 1,
    table,
    tables,
    words,
  })

  twoWaySearchWithHeaderNextHelper({
    cells,
    pages,
    possibleTables: possibleNextTables,
    searchPage: filePage + 1,
    table,
    tables,
    words,
  })

  possiblePreTables.reverse()

  return [possiblePreTables, possibleNextTables]
}

type TwoWaySearchWithHeaderPrevHelperArgs = SearchNextPageTablesArgs
const twoWaySearchWithHeaderPrevHelper = ({
  cells,
  pages,
  possibleTables,
  searchPage,
  table,
  tables,
  words,
}: TwoWaySearchWithHeaderPrevHelperArgs) => {
  const hasPage = pages.has(searchPage + '')
  if (!hasPage) return
  const tableHeader = getTableHeaders({ table, cells, words })
  const tableVertices = table.boundingPoly.vertices
  const [, width] = calculateHeightandWidth(
    tableVertices[0],
    tableVertices[1],
    tableVertices[2],
    tableVertices[3]
  )
  const topLeft = findTOpLeftPointFromVertices(tableVertices)
  if (!topLeft) return
  const tablesInPage = getAllTablesInPage({
    filePage: searchPage,
    pages,
    tables,
  })
  if (!tablesInPage.length) return
  else {
    const pageTable = tablesInPage[tablesInPage.length - 1]
    const pageTableHeader = getTableHeaders({
      table: pageTable,
      cells,
      words,
    })
    if (!pageTableHeader) return
    const pageTableVertices = pageTable.boundingPoly.vertices
    const [, pageTableWidth] = calculateHeightandWidth(
      pageTableVertices[0],
      pageTableVertices[1],
      pageTableVertices[2],
      pageTableVertices[3]
    )
    const pageTableTopLeft = findTOpLeftPointFromVertices(pageTableVertices)
    if (!pageTableTopLeft) return

    if (
      isDifferenceLessThan10Percent(width, pageTableWidth) &&
      isDifferenceLessThan10Percent(pageTableTopLeft.x, topLeft.x) &&
      tableHeader === pageTableHeader
    ) {
      pageTable.filePage = searchPage
      possibleTables.push(pageTable)

      if (tablesInPage.length === 1) {
        twoWaySearchWithHeaderPrevHelper({
          cells,
          pages,
          possibleTables,
          searchPage: searchPage - 1,
          table: pageTable,
          tables,
          words,
        })
      }
    }
  }
}

type TwoWaySearchWithHeaderNextHelperArgs = TwoWaySearchWithHeaderPrevHelperArgs
const twoWaySearchWithHeaderNextHelper = ({
  cells,
  pages,
  possibleTables,
  searchPage,
  table,
  tables,
  words,
}: TwoWaySearchWithHeaderNextHelperArgs) => {
  const hasPage = pages.has(searchPage + '')
  if (!hasPage) return
  // const tableHeader = getTableHeaders({ table, cells, words })
  const tableVertices = table.boundingPoly.vertices
  const [, width] = calculateHeightandWidth(
    tableVertices[0],
    tableVertices[1],
    tableVertices[2],
    tableVertices[3]
  )
  const topLeft = findTOpLeftPointFromVertices(tableVertices)
  if (!topLeft) return
  const tablesInPage = getAllTablesInPage({
    filePage: searchPage,
    pages,
    tables,
  })
  if (!tablesInPage.length) return
  else {
    const pageTable = tablesInPage[0]
    const pageTableVertices = pageTable.boundingPoly.vertices
    const [, pageTableWidth] = calculateHeightandWidth(
      pageTableVertices[0],
      pageTableVertices[1],
      pageTableVertices[2],
      pageTableVertices[3]
    )
    const pageTableTopLeft = findTOpLeftPointFromVertices(pageTableVertices)
    if (!pageTableTopLeft) return
    if (
      isDifferenceLessThan10Percent(width, pageTableWidth) &&
      isDifferenceLessThan10Percent(pageTableTopLeft.x, topLeft.x)
    ) {
      pageTable.filePage = searchPage
      possibleTables.push(pageTable)
      if (tablesInPage.length === 1) {
        twoWaySearchWithHeaderNextHelper({
          cells,
          pages,
          possibleTables,
          searchPage: searchPage + 1,
          table: pageTable,
          tables,
          words,
        })
      }
    }
  }
}

type SearchPrevPageTablesArgs = SearchNextPageTablesArgs
const searchPrevPageTables = ({
  cells,
  pages,
  possibleTables,
  searchPage,
  table,
  tables,
  words,
}: SearchPrevPageTablesArgs) => {
  const hasPage = pages.has(searchPage + '')
  if (!hasPage) return
  const tableHeader = getTableHeaders({ table, cells, words })
  const tableVertices = table.boundingPoly.vertices
  const [, width] = calculateHeightandWidth(
    tableVertices[0],
    tableVertices[1],
    tableVertices[2],
    tableVertices[3]
  )
  const topLeft = findTOpLeftPointFromVertices(tableVertices)
  if (!topLeft) return
  const tablesInPage = getAllTablesInPage({
    filePage: searchPage,
    pages,
    tables,
  })
  if (!tablesInPage.length) return
  else {
    const pageTable = tablesInPage[tablesInPage.length - 1]
    const pageTableHeader = getTableHeaders({
      table: pageTable,
      cells,
      words,
    })
    const pageTableVertices = pageTable.boundingPoly.vertices
    const [, pageTableWidth] = calculateHeightandWidth(
      pageTableVertices[0],
      pageTableVertices[1],
      pageTableVertices[2],
      pageTableVertices[3]
    )
    const pageTableTopLeft = findTOpLeftPointFromVertices(pageTableVertices)
    if (!pageTableTopLeft) return
    if (tableHeader) {
      if (
        isDifferenceLessThan10Percent(width, pageTableWidth) &&
        isDifferenceLessThan10Percent(pageTableTopLeft.x, topLeft.x) &&
        tableHeader === pageTableHeader
      ) {
        pageTable.filePage = searchPage
        possibleTables.push(pageTable)
        if (tablesInPage.length === 1) {
          searchPrevPageTables({
            cells,
            pages,
            possibleTables,
            searchPage: searchPage - 1,
            table: pageTable,
            tables,
            words,
          })
        }
      }
    } else {
      if (
        isDifferenceLessThan10Percent(width, pageTableWidth) &&
        isDifferenceLessThan10Percent(pageTableTopLeft.x, topLeft.x)
      ) {
        pageTable.filePage = searchPage
        possibleTables.push(pageTable)
        if (tablesInPage.length === 1 && !pageTableHeader) {
          searchPrevPageTables({
            cells,
            pages,
            possibleTables,
            searchPage: searchPage - 1,
            table: pageTable,
            tables,
            words,
          })
        }
      }
    }
  }
}

type TableCutPreprocessArgs = {
  table: TextBlock
  cells: Map<string, TextBlock>
  words: Map<string, TextBlock>
}
export const tableCutPreprocess = ({
  cells,
  table,
  words,
}: TableCutPreprocessArgs):
  | [string[][], string[][], number, number, number, TextBlock]
  | null => {
  const tableChildren = table.relationships?.find((re) => re.type === 'CHILD')
  if (!tableChildren) return null
  const tableCells = tableChildren.ids
    .filter((id) => cells.has(id))
    .map((id) => cells.get(id)!)
  const transformedTableCells = transformCells(tableCells, words)
  const [row, col] = findRowAndColFromCells(transformedTableCells)
  const matrix: string[][] = Array.from({ length: row }, () =>
    new Array(col).fill('')
  )
  const numberFormatMatrix: string[][] = Array.from({ length: row }, () =>
    new Array(col).fill('@')
  )
  forEach(
    transformedTableCells,
    (cell) =>
      (matrix[cell.rowIndex - 1][cell.columnIndex - 1] = cell.text
        ? cell.text
        : '')
  )
  return [matrix, numberFormatMatrix, row, col, table.filePage!, table]
}

export function deepCopyOcr(ocr: TestDataOcr): TestDataOcr {
  return ocr.map((arr) => ({
    ...arr,
    textAnnotations: arr.textAnnotations.map((block) => ({
      ...block,
      boundingPoly: {
        ...block.boundingPoly,
        vertices: block.boundingPoly.vertices.map((vertex) => ({ ...vertex })),
      },
    })),
  }))
}

export const getTablePage = (
  tableId: string,
  pages: Map<string, TextBlock>
): number => {
  for (const [k, v] of pages) {
    const p = v.relationships?.at(0)?.ids.find((id) => id === tableId)
    if (p) return Number(k)
  }
  return NaN
}

/**
 * Normalizes an ExtractlyFileWithOCR for efficient text searching using the Result pattern
 * @param file - The file with OCR data to normalize
 * @returns A Result containing either the normalized file structure or an error
 */
export const normalizeExtractlyFileWithOCR = (
  file: ExtractlyFileWithOCR
): Result<NormalizedFile, NormalizationError> => {
  // Input validation
  if (!file) {
    return err('INVALID_INPUT')
  }

  const ocr = file.ocrJson
  if (!ocr || !Array.isArray(ocr) || ocr.length === 0) {
    return err('EMPTY_OCR_DATA')
  }

  try {
    const dateCache = file.dateCache ?? []

    // Break down OCR data
    const [pages, , , lines, words] = breakDownOcr(ocr, 0)
    if (!lines || !words) {
      return err('OCR_BREAKDOWN_FAILED')
    }

    // Build date and number mappings
    const { dateMap, numberMap } = buildNumberAndDateMap(
      lines,
      words,
      dateCache
    )
    if (!dateMap || !numberMap) {
      return err('DATE_NUMBER_MAP_FAILED')
    }

    // Use Set for better performance when checking duplicates
    const valueMap = new Map<string, Set<string>>()

    const normalize = (text: string) =>
      normalizeString(text, [toLower, trim, normalizeHyphen])

    // Helper function to add values to the map
    const addToValueMap = (key: string, value: string) => {
      if (!valueMap.has(key)) {
        valueMap.set(key, new Set([value]))
      } else {
        valueMap.get(key)!.add(value)
      }
    }

    const getTextblockPageForEachFunc =
      (hashmap: Map<string, TextBlock>) => (textBlock: TextBlock) => {
        const [filePage, pageW, pageH] = getTextblockPage(
          pages,
          lines,
          textBlock
        )
        const block = {
          ...textBlock,
          filePage,
          pageW,
          pageH,
          fileId: file.id,
        } as TextBlock
        hashmap.set(block.id, block)
      }

    // Process text blocks efficiently
    const mappedValues = {
      mappedLines: new Map<string, TextBlock>(),
      mappedWords: new Map<string, TextBlock>(),
    }

    lines.forEach(getTextblockPageForEachFunc(mappedValues.mappedLines))
    words.forEach(getTextblockPageForEachFunc(mappedValues.mappedWords))

    const allBlocks = [
      ...mappedValues.mappedLines.values(),
      ...mappedValues.mappedWords.values(),
    ]
    for (const block of allBlocks) {
      const normalizedText = normalize(block.description)
      addToValueMap(normalizedText, block.id)
    }

    // Process date and number mappings
    dateMap.forEach(addToValueMap)
    numberMap.forEach(addToValueMap)

    // Convert Sets back to arrays for compatibility with existing interface
    const finalValueMap = new Map<string, string[]>()
    valueMap.forEach((valueSet, key) => {
      finalValueMap.set(key, Array.from(valueSet))
    })

    return ok({
      id: file.id,
      lines: mappedValues.mappedLines,
      words: mappedValues.mappedWords,
      values: finalValueMap,
    })
  } catch (error) {
    // Log the actual error for debugging while returning a generic error type
    console.error('Error normalizing ExtractlyFileWithOCR:', error)
    return err('OCR_BREAKDOWN_FAILED')
  }
}

/**
 * Helper function to process multiple files with proper error handling
 * @param files - Array of files to normalize
 * @returns Result containing successful normalizations and any errors encountered
 */
export const normalizeMultipleFiles = (
  files: ExtractlyFileWithOCR[]
): Result<
  {
    successful: NormalizedFile[]
    errors: { fileId: string; error: NormalizationError }[]
  },
  string
> => {
  if (!files || files.length === 0) {
    return err('No files provided')
  }

  const successful: NormalizedFile[] = []
  const errors: { fileId: string; error: NormalizationError }[] = []

  for (const file of files) {
    const result = normalizeExtractlyFileWithOCR(file)

    result.match(
      (normalizedFile) => successful.push(normalizedFile),
      (error) => errors.push({ fileId: file.id, error })
    )
  }

  return ok({ successful, errors })
}

/**
 * Creates a state machine representation from input data for data matching operations
 * @param input - 2D array of input values to be processed
 * @param inputValues - 2D array of raw input values (before preprocessing)
 * @param firstRowHeader - Flag indicating if first row should be treated as header (1) or not (0)
 * @returns Result containing either the row state machine or an error
 */
export const createRowStateMachineFromInput = (
  input: any[][],
  inputValues: any[][],
  firstRowHeader: 0 | 1
): Result<Row[], Error> => {
  if (!input || !inputValues) {
    return err(new Error('Input arrays cannot be null or undefined'))
  }

  if (input.length !== inputValues.length) {
    return err(
      new Error('Input and inputValues arrays must have the same length')
    )
  }

  if (input.length === 0) {
    return err(new Error('Input arrays cannot be empty'))
  }

  const settings = localStorage.getItem(USER_SETTINGS)
  const userSettings = settings ? (JSON.parse(settings) as UserSettings) : null
  const preProcessedInput = preProcessInput(
    input,
    inputValues,
    userSettings ? userSettings.dateFormat : DateFormat.US
  )
  const rows: Row[] = []
  for (let i = 0; i < input.length; i++) {
    if (i === 0 && firstRowHeader) {
      const headerRow: Row = {
        type: 'HEADER',
        cells: [...input[i]],
        locked: true,
        rowIdx: i,
        searchValues: [],
        vacancies: 0,
      }
      rows.push(headerRow)
    } else {
      const row: Row = {
        type: 'ROW',
        candidateHits: new Map(),
        cells: [...input[i].map((_) => null)],
        locked: false,
        rowIdx: i,
        searchValues: [...preProcessedInput[i].map((val) => val.toString())],
        vacancies: input[i].length,
      }
      rows.push(row)
    }
  }

  return ok(rows)
}

/**
 * Creates a mapping of search values to the positions (row and cell) that are waiting for those values
 * This is used to efficiently track which cells need specific values during data matching
 *
 * The function also handles special value types:
 * - Currency values: Creates additional waiters for numeric values extracted from currency strings
 * - Date values: Creates additional waiters for normalized date strings in AU format
 *
 * @param rowStates - Array of row state machines from createRowStateMachineFromInput
 * @returns Result containing either the value waiters map or an error
 *
 * @example
 * ```typescript
 * const rowStates = [
 *   { type: 'ROW', rowIdx: 0, searchValues: ['$100.50', '2023-12-25'] }
 * ]
 * const result = createValueWaiters(rowStates)
 * // Creates waiters for: '$100.50', '100.5', '2023-12-25', and normalized date
 * ```
 */
export const createValueWaiters = (
  rowStates: Row[]
): Result<Map<string, ValueWaiter[]>, Error> => {
  // Input validation
  if (!rowStates) {
    return err(new Error('rowStates cannot be null or undefined'))
  }

  if (!Array.isArray(rowStates)) {
    return err(new Error('rowStates must be a valid array'))
  }

  if (rowStates.length === 0) {
    return err(new Error('rowStates array cannot be empty'))
  }

  try {
    const valueWaiters = new Map<string, ValueWaiter[]>()

    rowStates
      .filter((row) => row.type === 'ROW')
      .forEach((row) => {
        row.searchValues.forEach((value, cellIndex) => {
          if (!value || value.trim() === '') {
            return
          }

          if (!valueWaiters.has(value)) {
            valueWaiters.set(value, [])
          }
          valueWaiters.get(value)!.push({
            rowIdx: row.rowIdx,
            cellIdx: cellIndex,
          })

          if (containsCurrencySymbol(value)) {
            const currencyValue = extractNumericValueFromAmount(value)
            // if value is a possible currency
            if (!isNaN(currencyValue)) {
              if (!valueWaiters.has(currencyValue.toString())) {
                valueWaiters.set(currencyValue.toString(), [])
              }
              valueWaiters.get(currencyValue.toString())!.push({
                rowIdx: row.rowIdx,
                cellIdx: cellIndex,
              })
            }
          }

          if (isValidDate(value, DateFormat.AU).valid) {
            const extractedDate = extractAndParseDateWithYearValidation(
              value,
              DateFormat.AU
            )
            // if value is a possible date
            if (extractedDate) {
              if (!valueWaiters.has(extractedDate)) {
                valueWaiters.set(extractedDate, [])
              }
              valueWaiters.get(extractedDate)!.push({
                rowIdx: row.rowIdx,
                cellIdx: cellIndex,
              })
            }
          }
        })
      })

    return ok(valueWaiters)
  } catch (error) {
    console.error('Error creating value waiters:', error)
    return err(new Error('Failed to create value waiters mapping'))
  }
}

/**
 * Matches file values to row states in the data matching process.
 * This function processes OCR-extracted values from a file and attempts to match them
 * with waiting positions in the row state machine, scoring matches based on how many
 * values from the same row can be found in the file.
 *
 * @param params - The matching parameters object
 * @param params.fileId - Unique identifier for the file being processed
 * @param params.fileValues - Map of normalized values to their corresponding OCR block IDs
 * @param params.valueWaiter - Map of values to positions (row/cell) waiting for those values
 * @param params.rowStatesMap - Map of row indices to their corresponding row state objects
 * @param params.fileValueSet - Pre-computed set of all file values for O(1) lookup performance
 * @param params.usedIds - Set of OCR block IDs that have already been matched to prevent duplicates
 * @returns Result indicating success or failure of the matching operation
 *
 * @example
 * ```typescript
 * const result = matchFileValuesToRowStates({
 *   fileId: "file123",
 *   fileValues: new Map([["john", ["id1", "id2"]], ["doe", ["id3"]]]),
 *   valueWaiter: new Map([["john", [{ rowIdx: 0, cellIdx: 0 }]]]),
 *   rowStatesMap: new Map([[0, rowState]]),
 *   fileValueSet: new Set(["john", "doe"]),
 *   usedIds: new Set()
 * });
 *
 * if (result.isOk()) {
 *   console.log("Matching completed successfully");
 * } else {
 *   console.error("Matching failed:", result.error);
 * }
 * ```
 */
export const matchFileValuesToRowStates = ({
  fileId,
  fileValues,
  valueWaiter,
  rowStatesMap,
  fileValueSet,
  usedIds,
}: {
  fileId: string
  fileValues: Map<string, string[]>
  valueWaiter: Map<string, ValueWaiter[]>
  rowStatesMap: Map<number, Row>
  usedIds: Set<string>
  fileValueSet: Set<string>
}): Result<void, Error> => {
  // Input validation
  if (!fileId || typeof fileId !== 'string') {
    return err(new Error('fileId must be a non-empty string'))
  }

  if (!fileValues || !(fileValues instanceof Map)) {
    return err(new Error('fileValues must be a valid Map'))
  }

  if (!valueWaiter || !(valueWaiter instanceof Map)) {
    return err(new Error('valueWaiter must be a valid Map'))
  }

  if (!rowStatesMap || !(rowStatesMap instanceof Map)) {
    return err(new Error('rowStatesMap must be a valid Map'))
  }

  if (!fileValueSet || !(fileValueSet instanceof Set)) {
    return err(new Error('fileValueSet must be a valid Set'))
  }

  if (!usedIds || !(usedIds instanceof Set)) {
    return err(new Error('usedIds must be a valid Set'))
  }

  try {
    for (const [value, ids] of fileValues) {
      // Validate that ids is an array
      if (!Array.isArray(ids)) {
        return err(new Error(`Invalid ids array for value: ${value}`))
      }

      const waiters = valueWaiter.get(value)
      if (!waiters) continue

      // Validate that waiters is an array
      if (!Array.isArray(waiters)) {
        return err(new Error(`Invalid waiters array for value: ${value}`))
      }

      const scoredRows: {
        position: ValueWaiter
        row: Extract<Row, { type: 'ROW' }>
        score: number
        fileHits: Map<number, CellValue>
      }[] = []

      // if value exists in valueWaiters
      for (const position of waiters) {
        // Validate position object structure
        if (
          !position ||
          typeof position.rowIdx !== 'number' ||
          typeof position.cellIdx !== 'number'
        ) {
          return err(new Error(`Invalid position object for value: ${value}`))
        }

        // Performance optimization: Use Map.get instead of Array.find for O(1) lookup
        const row = rowStatesMap.get(position.rowIdx)
        // if row not exist or row is locked or cell already exists, skip
        if (!row) continue
        if (row.locked) continue
        if (row.type !== 'ROW') continue // Ensure we have a ROW type with candidateHits
        if (row.cells[position.cellIdx]) continue

        // Validate row structure
        if (!row.searchValues || !Array.isArray(row.searchValues)) {
          return err(
            new Error(`Invalid searchValues for row: ${position.rowIdx}`)
          )
        }

        if (!row.candidateHits || !(row.candidateHits instanceof Map)) {
          return err(
            new Error(`Invalid candidateHits for row: ${position.rowIdx}`)
          )
        }

        // Performance optimization: Reuse pre-computed fileValueSet
        const score = row.searchValues.reduce(
          (count, v) => count + (fileValueSet.has(v) ? 1 : 0),
          0
        )

        // get File hit
        const fileHits =
          row.candidateHits.get(fileId) || new Map<number, CellValue>()
        if (fileHits.has(position.cellIdx)) {
          // if cell already exists, skip
          continue
        }
        scoredRows.push({ position, row, fileHits, score })
      }

      scoredRows.sort((a, b) => b.score - a.score)

      let cursor = 0
      for (const id of ids) {
        // Validate id is a string
        if (!id || typeof id !== 'string') {
          return err(new Error(`Invalid id: ${id} for value: ${value}`))
        }

        if (usedIds.has(id)) continue
        if (cursor >= scoredRows.length) break

        const { position, row, fileHits } = scoredRows[cursor++]
        fileHits.set(position.cellIdx, { id })
        row.candidateHits.set(fileId, fileHits)
        usedIds.add(id)
      }
    }

    return ok(undefined)
  } catch (error) {
    console.error('Error in matchFileValuesToRowStates:', error)
    return err(
      new Error(
        `Failed to match file values to row states: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    )
  }
}

/**
 * Matches files with OCR data to row states for data extraction and validation.
 * This function processes normalized files and attempts to match their values
 * with waiting row states, handling cell assignments and row locking logic.
 *
 * @param params - The parameters object containing input data
 * @param params.rowStates - Array of row state objects that need to be matched with file data
 * @param params.filesResult - Array of normalized files containing OCR values to match
 * @param params.valueWaiter - Map tracking which values are waiting for matches at specific positions
 *
 * @returns Result<void, Error> - Success result if all files are processed successfully,
 *                                or error result containing details about the failure
 *
 * @throws {Error} When input validation fails or file processing encounters errors
 *
 * @example
 * ```typescript
 * const result = matchFiles({
 *   rowStates: [
 *     { type: 'ROW', rowIdx: 0, cells: [], candidateHits: new Map(), locked: false, ... }
 *   ],
 *   filesResult: [
 *     { id: 'file1', values: new Map([['john', ['id1']]]), ... }
 *   ],
 *   valueWaiter: new Map([['john', [{ rowIdx: 0, cellIdx: 0 }]]])
 * });
 *
 * if (result.isOk()) {
 *   console.log("File matching completed successfully");
 * } else {
 *   console.error("File matching failed:", result.error.message);
 * }
 * ```
 *
 * @remarks
 * - Performance optimized with Map-based lookups for O(1) row access
 * - Prevents duplicate assignments using usedIds tracking
 * - Automatically locks rows when all cells are successfully matched
 * - Processes files sequentially to maintain consistent state
 */
export const matchFiles = ({
  rowStates,
  filesResult,
  valueWaiter,
  primaryIndices,
}: {
  rowStates: Row[]
  filesResult: NormalizedFile[]
  valueWaiter: Map<string, ValueWaiter[]>
  primaryIndices: number[]
}): Result<void, Error> => {
  // Input validation
  if (!Array.isArray(rowStates)) {
    return err(new Error('rowStates must be a valid array'))
  }

  if (!Array.isArray(filesResult)) {
    return err(new Error('filesResult must be a valid array'))
  }

  if (!valueWaiter || !(valueWaiter instanceof Map)) {
    return err(new Error('valueWaiter must be a valid Map'))
  }

  try {
    // Performance optimization: Create a Map for O(1) row lookup instead of O(n) find
    const rowStatesMap = new Map<number, Row>()

    // Validate each row state while building the map
    for (const row of rowStates) {
      if (!row || typeof row.rowIdx !== 'number') {
        return err(new Error('Invalid row state: missing or invalid rowIdx'))
      }

      if (
        !row.type ||
        (row.type !== 'ROW' &&
          row.type !== 'LOCKED_ROW' &&
          row.type !== 'HEADER')
      ) {
        return err(new Error(`Invalid row type, row: ${JSON.stringify(row)}`))
      }

      rowStatesMap.set(row.rowIdx, row)
    }

    const usedIds = new Set<string>()

    for (const file of filesResult) {
      // Validate file structure
      if (!file || !file.id || typeof file.id !== 'string') {
        return err(new Error('Invalid file: missing or invalid id'))
      }

      if (!file.values || !(file.values instanceof Map)) {
        return err(
          new Error(`Invalid file values for file ${file.id}: must be a Map`)
        )
      }

      // Performance optimization: Pre-compute file value set once per file
      const fileValueSet = new Set(file.values.keys())

      const matchResult = matchFileValuesToRowStates({
        fileId: file.id,
        fileValues: file.values,
        rowStatesMap,
        usedIds,
        fileValueSet,
        valueWaiter,
      })

      if (matchResult.isErr()) {
        return err(
          new Error(
            `Failed to match file ${file.id}: ${matchResult.error.message}`
          )
        )
      }

      // Process rows that have complete matches or can be upgraded to complete matches
      for (let i = 0; i < rowStates.length; i++) {
        if (rowStates[i].type === 'ROW') {
          const row = rowStates[i] as Extract<Row, { type: 'ROW' }>
          const fileHits = row.candidateHits.get(file.id)

          if (!fileHits) continue

          const fileHitCellIdx = Array.from(fileHits.keys())
          const isCompleteMatch = fileHits.size === row.cells.length

          // Handle primary indices logic
          if (primaryIndices.length > 0) {
            // Check if primary index cells are already filled
            const primaryIndicesFilled = primaryIndices.every(
              (idx) => row.cells[idx] != null
            )

            // Check if current file hits include primary indices
            const fileHasPrimaryIndices = primaryIndices.some((idx) =>
              fileHitCellIdx.includes(idx)
            )

            // If primary indices are not filled and current file also doesn't have primary indices,
            // compare accuracy to decide which one to use (we still want to return some values to users)
            if (!primaryIndicesFilled && !fileHasPrimaryIndices) {
              // Calculate current row accuracy (cells that are not null)
              const currentAccuracy =
                row.cells.reduce(
                  (count, cell) => count + (cell !== null ? 1 : 0),
                  0
                ) / row.cells.length

              // Calculate potential new accuracy if we use this file's hits
              const newRowCells = [...row.cells]
              for (const [cellIdx, cellValue] of fileHits) {
                newRowCells[cellIdx] = { id: cellValue.id }
              }
              const newAccuracy =
                newRowCells.reduce(
                  (count, cell) => count + (cell !== null ? 1 : 0),
                  0
                ) / newRowCells.length

              // Only proceed if new accuracy is higher than current accuracy
              if (newAccuracy <= currentAccuracy) {
                continue
              }

              // Clear existing cells before applying new matches for replacement
              for (let cellIdx = 0; cellIdx < row.cells.length; cellIdx++) {
                row.cells[cellIdx] = null
              }
            }

            // If primary indices are already filled, file2 can only replace if it has its own primary index AND higher accuracy
            if (primaryIndicesFilled) {
              // File2 must have its own primary index match to be considered for replacement
              if (!fileHasPrimaryIndices) {
                continue
              }

              // Calculate current row accuracy (cells that are not null)
              const currentAccuracy =
                row.cells.reduce(
                  (count, cell) => count + (cell !== null ? 1 : 0),
                  0
                ) / row.cells.length

              // Calculate potential new accuracy if we use this file's hits
              const newRowCells = [...row.cells]
              for (const [cellIdx, cellValue] of fileHits) {
                newRowCells[cellIdx] = { id: cellValue.id }
              }
              const newAccuracy =
                newRowCells.reduce(
                  (count, cell) => count + (cell !== null ? 1 : 0),
                  0
                ) / newRowCells.length

              // Only proceed if new accuracy is higher than current accuracy
              if (newAccuracy <= currentAccuracy) {
                continue
              }

              // Clear existing cells before applying new matches for replacement
              for (let cellIdx = 0; cellIdx < row.cells.length; cellIdx++) {
                row.cells[cellIdx] = null
              }
            }
          } else {
            // No primary indices - only process complete matches
            if (!isCompleteMatch) {
              continue
            }
          }

          // Validate file hits before processing
          for (const [cellIdx, cellValue] of fileHits) {
            if (typeof cellIdx !== 'number' || !cellValue || !cellValue.id) {
              return err(
                new Error(
                  `Invalid file hit data for file ${file.id}, row ${row.rowIdx}`
                )
              )
            }

            row.cells[cellIdx] = {
              id: cellValue.id,
            }
          }

          // Lock the row if all cells are matched
          if (isCompleteMatch) {
            rowStates[i] = {
              type: 'LOCKED_ROW',
              rowIdx: row.rowIdx,
              cells: [...row.cells],
              candidateHits: new Map(),
              locked: true,
              fileId: file.id,
              vacancies: 0,
              searchValues: [...row.searchValues],
            }
          }
        }
      }

      // Process partial matches for unlocked rows
      for (const row of rowStates) {
        if (row.type !== 'ROW' || row.locked) continue

        const fileHits = row.candidateHits.get(file.id)
        if (!fileHits || fileHits.size === 0) continue

        // Apply same primary index logic for partial matches
        if (primaryIndices.length > 0) {
          const fileHitCellIdx = Array.from(fileHits.keys())
          const fileHasPrimaryIndices = primaryIndices.some((idx) =>
            fileHitCellIdx.includes(idx)
          )

          // If primary indices are required and this file doesn't have them, skip partial matches
          if (!fileHasPrimaryIndices) {
            continue
          }
        }

        for (const [cellIdx, cellValue] of fileHits) {
          if (typeof cellIdx !== 'number' || !cellValue || !cellValue.id) {
            return err(
              new Error(
                `Invalid partial match data for file ${file.id}, row ${row.rowIdx}`
              )
            )
          }

          if (!row.cells[cellIdx]) {
            row.cells[cellIdx] = { id: cellValue.id }
            usedIds.add(cellValue.id)
          }
        }
      }
    }

    return ok(undefined)
  } catch (error) {
    console.error('Error in matchFiles:', error)
    return err(
      new Error(
        `Failed to match files: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    )
  }
}
